import React from 'react';
import { useTranslation } from 'react-i18next';
import ReportVorderseite from './ReportVorderseite';
import ReportTaetigkeiten from './ReportTaetigkeiten';
import ReportBilanz from './ReportBilanz';
import './ReportContent.css';

/**
 * Hauptkomponente für Report-Content
 * Kombiniert Vorderseite, Tätigkeiten und Bilanzen
 */
function ReportContent({
  reportOptions,
  vorderseiteData,
  measurements,
  analysis,
  ablaufDbData,
  decimalPlacesSollzeiten,
  formatTime,
  formatSollzeit,
  formatGrade,
  getAnalysis,
  updateZusammenstellungVorgabezeiten,
  ablaufDbName,
  setDeleteRowConfirm,
  VorderseiteForm,
  timeTypes,
  defaultTimeTypes,
  userEmail,
  userDeviceName
}) {
  const { t } = useTranslation();
  if (!analysis && (reportOptions.messpunkteBilanzZeitarten || reportOptions.bilanzKuerzel)) {
    return <div>{t('report.no_analysis_data')}</div>;
  }

  // Verwende die State-Variable timeTypes (aus localStorage) oder defaultTimeTypes
  const availableTimeTypes = timeTypes.length > 0 ? timeTypes : defaultTimeTypes;

  // Bestimme, ob Tätigkeiten angezeigt werden sollen
  const showTaetigkeiten = reportOptions.messpunkteDetail || reportOptions.messpunkteKompakt;
  const showBilanzen = reportOptions.messpunkteBilanzZeitarten || reportOptions.bilanzKuerzel;

  // Berechne Gesamtanzahl der Seiten für Seitennummern
  let totalPages = 0;
  let taetigkeitenPages = 0;
  if (reportOptions.vorderseite && vorderseiteData) totalPages = 1; // Vorderseite ist immer Seite 1
  if (showTaetigkeiten) {
    if (reportOptions.messpunkteKompakt) {
      // Kompakt: Schätze 1 Seite pro Activity (mindestens 1)
      const activities = [...new Set(measurements.filter(m => !m.isNote && !m.excluded).map(m => m.activity || 'Keine Tätigkeit'))];
      taetigkeitenPages = Math.max(1, activities.length);
    } else {
      // Detail: Chunk-Aufteilung mit ROWS_PER_PAGE = 24
      const measurementCount = measurements.filter(m => !m.isNote && !m.excluded).length;
      taetigkeitenPages = Math.max(1, Math.ceil(measurementCount / 24));
    }
    totalPages += taetigkeitenPages;
  }
  if (reportOptions.messpunkteBilanzZeitarten && analysis) totalPages++;
  if (reportOptions.bilanzKuerzel && analysis) totalPages++;

  return (
    <div id="report-content" className="report-a4-container">
      {/* 1. Vorderseite (Seite 1) */}
      {reportOptions.vorderseite && vorderseiteData && (
        <ReportVorderseite
          vorderseiteData={vorderseiteData}
          VorderseiteForm={VorderseiteForm}
          getAnalysis={getAnalysis}
          updateZusammenstellungVorgabezeiten={updateZusammenstellungVorgabezeiten}
          decimalPlacesSollzeiten={decimalPlacesSollzeiten}
          formatTime={formatTime}
          ablaufDbName={ablaufDbName}
          setDeleteRowConfirm={setDeleteRowConfirm}
          pageNumber={1}
          totalPages={totalPages}
          pageBreakAfter={showTaetigkeiten || showBilanzen ? 'always' : 'avoid'}
          userEmail={userEmail}
          userDeviceName={userDeviceName}
        />
      )}

      {/* 2. Tätigkeiten (kompakt oder detail) */}
      {(reportOptions.messpunkteKompakt || reportOptions.messpunkteDetail) && (
        <div className="report-section report-taetigkeiten-wrapper" style={{ 
          pageBreakAfter: showBilanzen ? 'always' : 'auto', 
          position: 'relative', 
          paddingBottom: '25mm' 
        }}>
          {reportOptions.messpunkteKompakt && (
            <ReportTaetigkeiten
              measurements={measurements}
              isKompakt={true}
              startPage={reportOptions.vorderseite && vorderseiteData ? 2 : 1}
              totalPages={totalPages}
              decimalPlacesSollzeiten={decimalPlacesSollzeiten}
              formatTime={formatTime}
              formatSollzeit={formatSollzeit}
              userEmail={userEmail}
              userDeviceName={userDeviceName}
            />
          )}
          {reportOptions.messpunkteDetail && !reportOptions.messpunkteKompakt && (
            <ReportTaetigkeiten
              measurements={measurements}
              isKompakt={false}
              startPage={reportOptions.vorderseite && vorderseiteData ? 2 : 1}
              totalPages={totalPages}
              decimalPlacesSollzeiten={decimalPlacesSollzeiten}
              formatTime={formatTime}
              formatSollzeit={formatSollzeit}
              userEmail={userEmail}
              userDeviceName={userDeviceName}
            />
          )}
        </div>
      )}

      {/* 3. Bilanzen */}
      {reportOptions.messpunkteBilanzZeitarten && analysis && (
        <ReportBilanz
          analysis={analysis}
          ablaufDbData={ablaufDbData}
          decimalPlaces={decimalPlacesSollzeiten}
          formatTime={formatTime}
          formatSollzeit={formatSollzeit}
          formatGrade={formatGrade}
          availableTimeTypes={availableTimeTypes}
          type="zeitarten"
          startPage={1 + taetigkeitenPages}
          totalPages={totalPages}
          pageBreakAfter={reportOptions.bilanzKuerzel ? 'always' : 'auto'}
          userEmail={userEmail}
          userDeviceName={userDeviceName}
        />
      )}

      {reportOptions.bilanzKuerzel && analysis && (
        <div className="report-section report-bilanz-kuerzel-wrapper" style={{ 
          position: 'relative', 
          paddingBottom: '25mm' 
        }}>
          <ReportBilanz
            analysis={analysis}
            ablaufDbData={ablaufDbData}
            decimalPlaces={decimalPlacesSollzeiten}
            formatTime={formatTime}
            formatSollzeit={formatSollzeit}
            formatGrade={formatGrade}
            type="kuerzel"
            startPage={totalPages}
            totalPages={totalPages}
            pageBreakAfter="auto"
            userEmail={userEmail}
            userDeviceName={userDeviceName}
          />
        </div>
      )}
    </div>
  );
}

export default ReportContent;
